import { debug } from "./debug";

/**
 * Chat Query Handler
 * 
 * This utility handles passing a search query to the chat iframe.
 * It tries multiple approaches to increase the chances of success.
 */

// Function to handle setting up the query submission
export function setupChatQueryHandler(iframe, query) {
  if (!iframe || !query) return;
  
  debug('Setting up chat query handler with query:', query);
  
  // 1. Try direct script injection when the iframe loads
  iframe.addEventListener('load', () => {
    debug('Iframe loaded, attempting to inject script');
    injectScript(iframe, query);
    
    // Also try sending a postMessage
    sendPostMessage(iframe, query);
  });
  
  // 2. Listen for messages from the iframe
  window.addEventListener('message', (event) => {
    // Check if it's our helper saying it's ready
    if (event.data && event.data.type === 'CLAUDE_INJECTOR_READY') {
      debug('Received ready message from injector');
      sendPostMessage(iframe, query);
    }
  });
  
  // 3. Try multiple times with increasing delays
  [1000, 2000, 3500, 5000].forEach(delay => {
    setTimeout(() => {
      debug(`Trying again after ${delay}ms`);
      injectScript(iframe, query);
      sendPostMessage(iframe, query);
    }, delay);
  });
}

// Function to inject a script into the iframe
function injectScript(iframe, query) {
  try {
    // Try to access the iframe's document
    const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
    
    if (!iframeDoc) {
      debug('Cannot access iframe document (likely due to CORS)');
      return;
    }
    
    // Create a script element
    const script = iframeDoc.createElement('script');
    script.textContent = `
      (function() {
        const query = ${JSON.stringify(query)};
        debug('Script injected with query:', query);
        
        // Function to find the input field
        function findInput() {
          // Try specific selectors first (based on screenshot)
          const input = document.querySelector('input[placeholder="Upišite svoju poruku..."]');
          if (input) return input;
          
          // Try other common selectors
          const selectors = [
            'textarea',
            'input[type="text"]',
            '[contenteditable="true"]',
            '[role="textbox"]'
          ];
          
          for (const selector of selectors) {
            const elements = document.querySelectorAll(selector);
            if (elements.length > 0) {
              return elements[elements.length - 1]; // Usually the last one
            }
          }
          
          return null;
        }
        
        // Function to find the send button
        function findSendButton() {
          const buttons = document.querySelectorAll('button');
          for (const button of buttons) {
            if (
              button.textContent.match(/send|submit|pošalji/i) ||
              button.classList.contains('send-button') ||
              button.type === 'submit' ||
              button.querySelector('svg')
            ) {
              return button;
            }
          }
          return null;
        }
        
        // Function to set the input value and submit
        function submitMessage() {
          const input = findInput();
          if (!input) {
            debug('Input field not found');
            return false;
          }
          
          debug('Input field found, setting value');
          
          // Set the value based on the type of input
          if (input.tagName.toLowerCase() === 'input' || input.tagName.toLowerCase() === 'textarea') {
            input.value = query;
            input.dispatchEvent(new Event('input', { bubbles: true }));
          } else if (input.getAttribute('contenteditable') === 'true') {
            input.textContent = query;
            input.dispatchEvent(new Event('input', { bubbles: true }));
          }
          
          // Try to find and click the send button
          const sendButton = findSendButton();
          if (sendButton) {
            debug('Send button found, clicking it');
            sendButton.click();
          } else {
            debug('No send button found, simulating Enter key');
            input.dispatchEvent(new KeyboardEvent('keydown', {
              key: 'Enter',
              code: 'Enter',
              keyCode: 13,
              which: 13,
              bubbles: true
            }));
          }
          
          return true;
        }
        
        // Try to submit the message
        setTimeout(submitMessage, 500);
      })();
    `;
    
    // Add the script to the iframe
    iframeDoc.body.appendChild(script);
    debug('Script injected successfully');
  } catch (e) {
    debug('Error injecting script:', e);
  }
}

// Function to send a message to the iframe using postMessage
function sendPostMessage(iframe, query) {
  try {
    debug('Sending postMessage to iframe with query:', query);
    
    // Send multiple message formats to increase chances of success
    iframe.contentWindow.postMessage({
      type: 'CHAT_QUERY',
      query: query,
      message: query,
      text: query
    }, '*');
    
    // Also try a simpler format
    iframe.contentWindow.postMessage({
      query: query
    }, '*');
    
    // And even simpler
    iframe.contentWindow.postMessage(query, '*');
    
    debug('Message sent to iframe');
  } catch (e) {
    debug('Error sending postMessage:', e);
  }
}

// Function to create a script URL with the query embedded
export function createScriptURL(baseURL, query) {
  // Create a data URL with our script embedded
  const scriptContent = `
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
    } else {
      init();
    }
    
    // Also try after a delay in case UI loads dynamically
    setTimeout(init, 1000);
    setTimeout(init, 2000);
    
    function init() {
      const query = ${JSON.stringify(query)};
      debug('Initializing with query:', query);
      
      function findInputAndSubmit() {
        // Find input field
        const input = document.querySelector('input[placeholder="Upišite svoju poruku..."]') || 
                      document.querySelector('input[type="text"]') || 
                      document.querySelector('textarea');
        
        if (!input) {
          debug('Input field not found yet');
          return false;
        }
        
        // Set value
        debug('Setting input value');
        input.value = query;
        input.dispatchEvent(new Event('input', { bubbles: true }));
        
        // Try to submit
        const sendButton = document.querySelector('button[type="submit"]') || 
                           [...document.querySelectorAll('button')].find(b => 
                              b.textContent.match(/send|submit|pošalji/i) || 
                              b.querySelector('svg'));
        
        if (sendButton) {
          debug('Clicking send button');
          sendButton.click();
        } else {
          debug('Simulating Enter key');
          input.dispatchEvent(new KeyboardEvent('keydown', {
            key: 'Enter',
            code: 'Enter',
            keyCode: 13,
            which: 13,
            bubbles: true
          }));
        }
        
        return true;
      }
      
      // Try a few times
      if (!findInputAndSubmit()) {
        setTimeout(findInputAndSubmit, 500);
        setTimeout(findInputAndSubmit, 1500);
      }
    }
  `;
  
  // Create a data URL
  const dataURL = `data:text/javascript,${encodeURIComponent(scriptContent)}`;
  
  // Now construct the full URL with our script
  return `${baseURL}${baseURL.includes('?') ? '&' : '?'}scriptSrc=${encodeURIComponent(dataURL)}`;
}
